import type { GIBBS_FREE_ENERGY_CALCULATOR_STRINGS } from "@/components/tools/gibbs-free-energy-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";

type Strings = typeof GIBBS_FREE_ENERGY_CALCULATOR_STRINGS;

const WIDTH = 480;
const HEIGHT = 260;
const LEFT = 64;
const RIGHT = 20;
const TOP = 20;
const BOTTOM = 44;
const MIN_RANGE = 500;
const ROUND_TO = 100;
const HEADROOM = 1.6;
const Y_PAD = 0.12;

/** ΔG against temperature: a straight line from ΔH (at 0 K) with slope −ΔS. */
export function GibbsPlot({
  h,
  s,
  t,
  crossover,
  strings,
}: {
  /** J/mol */
  h: number;
  /** J/(mol·K) */
  s: number;
  /** K */
  t: number;
  crossover: number | null;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const reach = Math.max(crossover ?? 0, t) * HEADROOM;
  const xMax = Math.max(MIN_RANGE, Math.ceil(reach / ROUND_TO) * ROUND_TO);
  const gAt = (kelvin: number) => (h - kelvin * s) / 1000;
  const ends = [gAt(0), gAt(xMax), gAt(t), 0];
  const lo = Math.min(...ends);
  const hi = Math.max(...ends);
  const pad = (hi - lo || 1) * Y_PAD;
  const yMin = lo - pad;
  const yMax = hi + pad;
  const x = (kelvin: number) => LEFT + (kelvin / xMax) * (WIDTH - LEFT - RIGHT);
  const y = (kj: number) =>
    TOP + ((yMax - kj) / (yMax - yMin)) * (HEIGHT - TOP - BOTTOM);
  const num = (value: number) =>
    fmt(value, { maximumFractionDigits: Math.abs(value) < 10 ? 1 : 0 });
  const yTicks = [lo, 0, hi].filter(
    (value, index, all) =>
      all.findIndex((other) => Math.abs(other - value) < (yMax - yMin) / 12) ===
      index
  );
  const xTicks = [0, xMax / 2, xMax];
  return (
    <figure className="space-y-2">
      <svg
        aria-label={strings.plotLabel}
        className="w-full max-w-xl"
        role="img"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      >
        <title>{strings.plotLabel}</title>
        <line
          className="stroke-border"
          x1={LEFT}
          x2={LEFT}
          y1={TOP}
          y2={HEIGHT - BOTTOM}
        />
        <line
          className="stroke-border"
          x1={LEFT}
          x2={WIDTH - RIGHT}
          y1={HEIGHT - BOTTOM}
          y2={HEIGHT - BOTTOM}
        />
        <line
          className="stroke-muted-foreground"
          strokeDasharray="4 4"
          x1={LEFT}
          x2={WIDTH - RIGHT}
          y1={y(0)}
          y2={y(0)}
        />
        <line
          className="stroke-primary"
          strokeWidth={2.5}
          x1={x(0)}
          x2={x(xMax)}
          y1={y(gAt(0))}
          y2={y(gAt(xMax))}
        />
        {crossover !== null && crossover <= xMax ? (
          <g>
            <line
              className="stroke-muted-foreground"
              strokeDasharray="2 3"
              x1={x(crossover)}
              x2={x(crossover)}
              y1={TOP}
              y2={HEIGHT - BOTTOM}
            />
            <circle
              className="fill-background stroke-primary"
              cx={x(crossover)}
              cy={y(0)}
              r={5}
              strokeWidth={2}
            />
            <text
              className="fill-foreground text-[11px]"
              textAnchor="middle"
              x={x(crossover)}
              y={TOP - 6}
            >
              {`${strings.plotCrossover} ${fmt(crossover, { maximumFractionDigits: 1 })} K`}
            </text>
          </g>
        ) : null}
        <circle className="fill-primary" cx={x(t)} cy={y(gAt(t))} r={5} />
        <text
          className="fill-foreground text-[11px]"
          textAnchor={t > xMax / 2 ? "end" : "start"}
          x={x(t) + (t > xMax / 2 ? -9 : 9)}
          y={y(gAt(t)) - 9}
        >
          {strings.plotNow}
        </text>
        {yTicks.map((value) => (
          <text
            className="fill-muted-foreground text-[11px]"
            dominantBaseline="middle"
            key={`y${value}`}
            textAnchor="end"
            x={LEFT - 6}
            y={y(value)}
          >
            {num(value)}
          </text>
        ))}
        {xTicks.map((value) => (
          <text
            className="fill-muted-foreground text-[11px]"
            key={`x${value}`}
            textAnchor="middle"
            x={x(value)}
            y={HEIGHT - BOTTOM + 16}
          >
            {num(value)}
          </text>
        ))}
        <text
          className="fill-muted-foreground text-[11px]"
          textAnchor="middle"
          x={(LEFT + WIDTH - RIGHT) / 2}
          y={HEIGHT - 6}
        >
          {strings.plotX}
        </text>
        <text
          className="fill-muted-foreground text-[11px]"
          textAnchor="middle"
          transform={`rotate(-90 14 ${(TOP + HEIGHT - BOTTOM) / 2})`}
          x={14}
          y={(TOP + HEIGHT - BOTTOM) / 2}
        >
          {strings.plotY}
        </text>
      </svg>
    </figure>
  );
}
